/**
 * The analytics vocabulary: what an event may carry, what a bucket is, what a
 * session is, and which numbers may be added to which.
 *
 * ## Why any of this is in `shared` rather than next to the rollup
 *
 * Four places have to agree about a session and a bucket boundary, and three
 * of them do not exist yet: the rollup that writes the aggregates, the reads
 * the dashboard draws from, the realtime counters in Redis, and eventually the
 * funnel that asks "did these two events happen in the same session". A
 * thirty-minute gap defined twice is a product that reports two different
 * session counts for the same day and cannot say which one is wrong. So it is
 * defined once, here, as a number and a predicate, and everything else calls
 * them.
 *
 * ## The rule that this file exists to make unavoidable
 *
 * **A unique-visitor count is exact inside one bucket and meaningless across
 * two.** Panel's visitor id is `sha256(daily_salt || ip || user_agent ||
 * site_id)` and the salt is destroyed every night, so the same person is a
 * different id tomorrow. Adding Monday's uniques to Tuesday's therefore counts
 * a returning visitor twice, and there is no correction available — the
 * information needed to deduplicate was deliberately destroyed. The same is
 * true of two hours inside one day, for the plainer reason that a visitor can
 * appear in both.
 *
 * "Unique visitors over 30 days" is consequently not a number Panel can
 * produce, will produce, or should display. That is a property of the identity
 * model and not a gap in the implementation: it is the same property that
 * removes the consent banner from the customer's site. {@link combineBuckets}
 * returns `null` for it rather than a plausible sum, so a caller has to decide
 * what to render instead of silently shipping a wrong number.
 */
import { scrubPath, scrubUrlsInText } from '../utils/scrub.util.js';

// ---------------------------------------------------------------------------
// What an event is allowed to carry
// ---------------------------------------------------------------------------

/**
 * The name of the one event every site sends without asking for it.
 *
 * Stored in the same column as a custom event name rather than in a boolean or
 * a separate table: a pageview *is* an event, the dashboard's "top events" list
 * wants both, and a funnel later has to step across the two kinds without a
 * union query.
 *
 * The leading `$` is a reserved namespace, and it is the reason this is a wire
 * contract rather than a label. A customer event named `$pageview` would be
 * indistinguishable from ours in every count the dashboard draws, so the
 * browser refuses the prefix on custom names and the ingest route refuses it
 * again — see `packages/tracker/src/props.ts`. Changing the spelling here
 * silently splits every site's history in two at the deploy, so it does not
 * change; a second Panel-owned event is `$something-else`.
 */
export const PAGEVIEW_EVENT_NAME = '$pageview';

/** The prefix Panel keeps for its own event names. Nothing a customer sends
 *  may start with it. */
export const RESERVED_EVENT_NAME_PREFIX = '$';

/**
 * A click on a link that leaves the site.
 *
 * ## Why this is page behaviour and not person behaviour
 *
 * The three names below and {@link PAGEVIEW_EVENT_NAME} are all Panel's own,
 * and they are all measurements of a PAGE. "Four hundred people left for
 * `instagram.com` yesterday" is a fact about a link in a template; it needs no
 * identifier that survives the night, it is identical for two visitors who
 * clicked the same link, and it is gone at midnight with the row it sits on.
 * Nothing here moves the consent line — which is the test any new measurement
 * in this product has to pass before its usefulness is even discussed.
 *
 * ## Why an event name and not a column or a table
 *
 * `events` already carries a name, a path, a referrer host, a country and a
 * device for every row, and every one of those is exactly as interesting for an
 * outbound click as it is for a page view — "which page did they leave from",
 * "which campaign sent the people who leave". A separate table would have had
 * to grow all of it again, would have needed its own pruner in
 * `TELEMETRY_PRUNERS`, and would have been a second thing to keep the manifest
 * honest about. The one field an outbound click needs that a page view does not
 * is where it went, and that is `events.destination`.
 */
export const OUTBOUND_EVENT_NAME = '$outbound';

/** A click on a link to a file — see {@link DOWNLOAD_EXTENSION_PATTERN}. */
export const DOWNLOAD_EVENT_NAME = '$download';

/**
 * A page the site's own server answered `404` for.
 *
 * Distinct from the page view that accompanies it rather than replacing it: the
 * browser did render a document, the visitor did read something, and a 404 page
 * that silently failed to count would make the totals disagree with the
 * customer's own server logs for a reason nobody could find.
 */
export const NOT_FOUND_EVENT_NAME = '$404';

/**
 * Every event name Panel itself may write, and therefore the complete list of
 * names the ingest boundary accepts with the reserved prefix on them.
 *
 * The list is what makes `RESERVED_EVENT_NAME_PREFIX` enforceable in one place
 * instead of four: a customer's event may not start with `$`, and a payload
 * claiming a `$` name that is not on this list is refused. Without the list,
 * "is this one of ours" was a comparison against a single constant and adding
 * a second Panel-owned name meant finding every such comparison.
 */
export const PANEL_EVENT_NAMES = [
  PAGEVIEW_EVENT_NAME,
  OUTBOUND_EVENT_NAME,
  DOWNLOAD_EVENT_NAME,
  NOT_FOUND_EVENT_NAME,
] as const;

export function isPanelEventName(value: unknown): boolean {
  return typeof value === 'string' && (PANEL_EVENT_NAMES as readonly string[]).includes(value);
}

/**
 * The file extensions a link has to end in for a click on it to be a download.
 *
 * ## A closed list, for the same reason the UTM parameters are one
 *
 * There is no SHAPE that separates a file from a page — `/pricing` and
 * `/pricing.pdf` differ by a literal — so this is a list of literals and never
 * a heuristic over "does the last segment contain a dot". A heuristic there
 * counts `/v1.2/changelog` as a download of a `2/changelog` file.
 *
 * Images are deliberately absent. A page that links its own illustrations
 * would report a "download" for every reader who clicked one to enlarge it,
 * which is a number about a lightbox rather than about a file anybody wanted.
 * The list is documents, archives, installers and media — the things a visitor
 * takes away and a merchant paid to produce.
 *
 * It is matched against `URL.pathname`, which never contains a query string, so
 * `/pricing?file=.pdf` is a page and not a download. It is NOT matched against
 * the scrubbed path, and that is the subtle half — see {@link scrubFilePath}.
 */
export const DOWNLOAD_EXTENSION_PATTERN =
  /\.(?:pdf|docx?|xlsx?|pptx?|odt|ods|odp|rtf|txt|csv|tsv|json|xml|zip|rar|7z|gz|tgz|bz2|tar|dmg|pkg|exe|msi|apk|deb|rpm|iso|mp3|wav|flac|ogg|m4a|mp4|m4v|mov|avi|mkv|webm|wmv|epub|mobi)$/i;

/**
 * A path to a file, redacted like any other path but with its extension kept.
 *
 * ## The bug this function exists because of
 *
 * `scrubPath` collapses a segment that looks like an identifier, and one of the
 * shapes it collapses is "over sixteen characters mixing letters and digits" —
 * which is `catalogue-2026.pdf`, `rapport-annuel-2025.pdf` and a good half of
 * the real filenames a merchant publishes. Scrubbing first and then asking
 * "does this end in a file extension" therefore answered NO for exactly the
 * files worth counting, and the downloads table was silently missing them.
 *
 * So the extension is read from the raw path, the redaction runs as it always
 * does, and the extension is put back on the collapsed segment:
 * `/files/catalogue-2026.pdf` becomes `/files/:id.pdf`. That is a row a reader
 * can act on — it says a PDF in `/files` is being taken — while the filename,
 * which is the part that could have carried somebody's order number, is gone.
 *
 * Both sides call this: the browser composes the destination and the ingest
 * boundary re-derives it from a payload it does not trust. A rule with two
 * implementations is a rule that drifts, and the drift here would be silent on
 * both sides.
 */
export function scrubFilePath(raw: string, max: number): string {
  const extension = DOWNLOAD_EXTENSION_PATTERN.exec(raw);
  const scrubbed = scrubPath(raw, max) || '/';
  if (extension === null || DOWNLOAD_EXTENSION_PATTERN.test(scrubbed)) return scrubbed;
  return (scrubbed + extension[0]).slice(0, max);
}

/** A custom event name. Long enough for `checkout.payment.succeeded`. */
export const MAX_EVENT_NAME_LENGTH = 64;

/**
 * The clamp on `events.destination` — where an outbound click or a download
 * went, as a host with a scrubbed path glued to it.
 *
 * It is not `MAX_HOST_LENGTH + MAX_PATH_LENGTH`, which is what an addition
 * would give and which would be 373 characters in a `dimension_stats.value`
 * column that holds 255. This number is the one that has to survive the trip
 * INTO the breakdown table, because that is where a customer reads it, so it is
 * set from that column's width and not from the sum of its parts.
 */
export const MAX_DESTINATION_LENGTH = 200;

/**
 * The host half of a destination, or null.
 *
 * `instagram.com/emnashop` → `instagram.com`. Written here rather than in the
 * rollup because the browser composes the value and the rollup takes it apart,
 * and a split rule that lives on only one of those two sides is a rule that
 * stops matching the day the join changes.
 */
export function destinationHost(destination: string | null | undefined): string | null {
  if (typeof destination !== 'string' || destination.length === 0) return null;
  const slash = destination.indexOf('/');
  const host = slash === -1 ? destination : destination.slice(0, slash);
  return host.length === 0 ? null : host;
}

/**
 * The lowercase extension of a destination, without its dot, or null.
 *
 * `shop.example/docs/catalogue.pdf` → `pdf`. Bounded by the pattern above
 * rather than by "whatever follows the last dot": this value becomes a row in a
 * breakdown table written from a public endpoint, and an unbounded reading of
 * it is an unbounded set of values for a stranger to fill it with.
 */
export function destinationExtension(destination: string | null | undefined): string | null {
  if (typeof destination !== 'string') return null;
  const match = DOWNLOAD_EXTENSION_PATTERN.exec(destination);
  return match === null ? null : (match[0] as string).slice(1).toLowerCase();
}

/**
 * The clamp on a stored path. Mirrors the cap `scrubPath` is called with in the
 * shared redaction util, and for the same reason: a path longer than this is a
 * serialised payload rather than a route, and storing it whole is storing
 * whatever the page put in it.
 */
export const MAX_PATH_LENGTH = 120;

/** A DNS name's real ceiling. Referrer host and site domain both. */
export const MAX_HOST_LENGTH = 253;

/** One UTM parameter. Campaign names are long; nothing legitimate is longer.
 *  All five UTM parameters have a column and a dimension. */
export const MAX_UTM_LENGTH = 120;

/** `FR`, `DE`, `ZZ`. ISO 3166-1 alpha-2, resolved from the IP and then the IP
 *  is gone — see the schema header. */
export const COUNTRY_CODE_LENGTH = 2;

/** A family name (`Chrome`, `Safari`, `Windows`), never a version string. A
 *  version is one more bit of entropy on a fingerprint we are trying not to
 *  build, and nobody has ever acted on it. */
export const MAX_UA_FAMILY_LENGTH = 32;

/**
 * The device classes.
 *
 * Three of them are the three a layout decision is made for, and there is no
 * fourth: every extra class is a finer bucket for somebody to be alone in.
 * `bot` is not a device at all — it is the ingest path saying "this was not a
 * person", kept as a value rather than dropped so that a site whose numbers
 * doubled overnight can see why in one row instead of guessing.
 *
 * An unrecognised user agent stores `null`, not `'other'`: absent is honest,
 * `'other'` is a claim.
 */
export const DEVICE_TYPES = ['desktop', 'mobile', 'tablet', 'bot'] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];

export function isDeviceType(value: unknown): value is DeviceType {
  return typeof value === 'string' && (DEVICE_TYPES as readonly string[]).includes(value);
}

/**
 * A visitor id as it is stored: 128 bits of a SHA-256, lowercase hex.
 *
 * Not allocated — derived. `lib/visitor-hash.ts` in the API computes
 * `sha256(daily_salt || ip || user_agent || site_id)` and truncates to this
 * width; the length lives here rather than there because the column width, the
 * validator and the hash all have to agree, and only one of the three is in the
 * file that does the hashing.
 *
 * 128 bits is far beyond any birthday bound that applies: one site would need
 * on the order of 10^19 visitors in a single day before a collision became
 * likely, and a collision merges two visitors into one rather than revealing
 * anything about either. Truncating also discards information rather than
 * adding any, which is the direction this product errs in.
 *
 * Validated on the way in so that a bug in the ingest path shows up as a
 * rejected write rather than as a column holding something reversible.
 */
export const VISITOR_ID_LENGTH = 32;
const VISITOR_ID_PATTERN = /^[0-9a-f]{32}$/;

export function isVisitorId(value: unknown): value is string {
  return typeof value === 'string' && VISITOR_ID_PATTERN.test(value);
}

// ---------------------------------------------------------------------------
// Event properties — the extensibility the brief asked for, bounded
// ---------------------------------------------------------------------------

/**
 * A custom event's properties: a flat bag of scalars, stored as JSON.
 *
 * ## Why flat and why capped
 *
 * The brief wants funnels and richer events to land later "without a painful
 * migration", which is what a JSON column buys. What a JSON column does not
 * buy is safety: it is an unvalidated hole in the middle of a schema whose
 * entire promise is about what it does not hold, written to by a public
 * endpoint anybody can POST to. So the hole has a shape.
 *
 * Flat, because a nested object is a place for a whole user record to arrive
 * in one field. Scalars only, for the same reason. Sixteen keys, because a
 * seventeenth is a dump rather than a measurement. And every string value goes
 * through the same redaction a path does, because `{"next":"/reset?token=…"}`
 * is exactly how a URL gets into analytics after the path rule has stopped
 * looking.
 *
 * ## What it must never become
 *
 * A property named `user_id`, `email` or anything else stable across days is
 * the persistent identifier this product does not have, smuggled in through
 * the one untyped column. Nothing here can detect that by name — a customer
 * can call it `x` — so the defence is the one that does not depend on naming:
 * these values are never joined, never indexed for lookup, and never read back
 * per visitor. They are group-by material. A feature that reads a property for
 * one visitor across two days is out of scope, and is to be refused in writing.
 */
export type EventPropValue = string | number | boolean;
export type EventProps = Readonly<Record<string, EventPropValue>>;

export const MAX_EVENT_PROPS = 16;
export const MAX_PROP_KEY_LENGTH = 32;
export const MAX_PROP_VALUE_LENGTH = 120;

/** `a-z0-9_.-`, because a key becomes a column heading and a group-by target. */
const PROP_KEY_PATTERN = /^[a-z0-9][a-z0-9_.-]*$/i;

/**
 * Whatever arrived, reduced to something storable, or `null` for "nothing worth
 * storing".
 *
 * Drops rather than throws, one key at a time. An event that carries fifteen
 * good properties and one nested object is still a measurement; refusing the
 * whole event would lose a pageview to a typo in a customer's tag, and this
 * runs on a path whose only honest answer is 204 either way.
 */
export function sanitizeEventProps(raw: unknown): EventProps | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, EventPropValue> = {};
  let kept = 0;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (kept >= MAX_EVENT_PROPS) break;
    if (key.length === 0 || key.length > MAX_PROP_KEY_LENGTH || !PROP_KEY_PATTERN.test(key))
      continue;
    if (typeof value === 'boolean') {
      out[key] = value;
    } else if (typeof value === 'number') {
      // NaN and Infinity are not JSON and would come back as `null` from
      // Postgres, turning a dropped value into a stored one.
      if (!Number.isFinite(value)) continue;
      out[key] = value;
    } else if (typeof value === 'string') {
      const cleaned = scrubUrlsInText(value).slice(0, MAX_PROP_VALUE_LENGTH);
      if (cleaned.length === 0) continue;
      out[key] = cleaned;
    } else {
      continue;
    }
    kept += 1;
  }
  return kept === 0 ? null : out;
}

// ---------------------------------------------------------------------------
// Campaign parameters
// ---------------------------------------------------------------------------

/**
 * One UTM value, reduced to something storable, or `null`.
 *
 * ## This is free text off a public URL, and it is read by three consumers
 *
 * A `utm_campaign` is whatever was typed into the address bar of whoever
 * clicked the link, which on a public write path means whatever an attacker
 * chose. It ends up in a `varchar(120)` column, in a `GROUP BY`, and on a
 * screen. So three things are done to it and each answers one of those:
 *
 *  - **Control characters are removed**, not escaped. A `\n` or a ` ` in
 *    a campaign name is never a campaign name; it is somebody trying to break
 *    a log line, a CSV export or a terminal. There is no legitimate value this
 *    loses.
 *  - **It is clamped to {@link MAX_UTM_LENGTH}**, which is also the column
 *    width, so a long value is a short row rather than a failed insert — and a
 *    failed insert on the ingest path costs a whole tenant's batch.
 *  - **It is lowercased.** `Spring_Sale` and `spring_sale` are one campaign to
 *    the person who ran it and two rows to a `GROUP BY`, and a breakdown that
 *    splits one campaign across three casings is the wrong number this
 *    function exists to prevent. The cost is that a marketer who capitalised
 *    deliberately does not see their capitals back; that is visibly odd,
 *    where a silently halved campaign is not.
 *
 * What it deliberately does **not** do is strip or escape markup. `<script>`
 * is a perfectly ordinary sequence of characters and mangling it here would
 * make the stored value disagree with the customer's own campaign report.
 * Escaping belongs to whoever renders it, once, in the encoding of the place
 * it is being rendered into — the dashboard is React and escapes text nodes
 * and attributes by construction, and there is a test that says so. A
 * sanitiser here would be a second, weaker defence in the wrong layer that
 * invites the first one to be forgotten.
 */
export function sanitizeUtmValue(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  // C0, DEL and C1. Written as an explicit class rather than a `\p{C}` escape
  // because the latter also matches unassigned code points and private-use
  // areas, which are legitimate in a campaign name in a script we do not read.
  const cleaned = raw
    // biome-ignore lint/suspicious/noControlCharactersInRegex: removing them is the point
    .replace(/[ --]/g, '')
    .trim()
    .toLowerCase()
    .slice(0, MAX_UTM_LENGTH)
    // A clamp can leave trailing space behind; a value that differs from
    // another only by it is another split row.
    .trim();
  return cleaned.length === 0 ? null : cleaned;
}

// ---------------------------------------------------------------------------
// Buckets
// ---------------------------------------------------------------------------

/**
 * The two grains the aggregates are stored at.
 *
 * A string union and not a Postgres enum, here and for every other vocabulary
 * in this file. Adding a value to a Postgres enum is a migration that takes a
 * lock; adding one to a union is a deploy. The brief asks for funnels and
 * richer breakdowns to land "without a painful migration", and this is most of
 * what that means in practice.
 */
export const GRANULARITIES = ['hour', 'day'] as const;
export type Granularity = (typeof GRANULARITIES)[number];

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * The instant a bucket starts, **in UTC**.
 *
 * UTC and not the tenant's timezone, deliberately and with a cost. A tenant in
 * Paris reading "yesterday" gets a day that ended at 01:00 their time, which is
 * wrong for them and which a later lot will have to fix by re-bucketing on
 * read. Storing in local time instead would have moved the problem rather than
 * solved it: buckets would change width twice a year, two tenants could not
 * share a row, and the day a tenant changes their timezone every historical
 * bucket becomes unexplainable. UTC is the only boundary that is the same
 * boundary tomorrow.
 *
 * It is also the boundary the salt rotates on, which is what makes a UTC day
 * the largest window a unique-visitor count can be exact over.
 */
export function bucketStartOf(at: Date, granularity: Granularity): Date {
  const ms = at.getTime();
  const width = granularity === 'hour' ? HOUR_MS : DAY_MS;
  // Both widths divide the epoch exactly, so flooring the epoch is the same as
  // zeroing the smaller fields and is immune to how a Date prints locally.
  return new Date(Math.floor(ms / width) * width);
}

/** The instant the next bucket starts — the exclusive end of this one. */
export function bucketEndOf(bucketStart: Date, granularity: Granularity): Date {
  return new Date(bucketStart.getTime() + (granularity === 'hour' ? HOUR_MS : DAY_MS));
}

/** The bucket after this one. Same thing as {@link bucketEndOf}, named for the
 *  caller that is walking forwards rather than asking for a window. */
export function nextBucketStart(bucketStart: Date, granularity: Granularity): Date {
  return bucketEndOf(bucketStart, granularity);
}

/**
 * Is this bucket over?
 *
 * The rollup may only ever aggregate a closed bucket. Half an hour written into
 * the hour's row is a number that is right for four minutes and then silently
 * wrong for ever, because nothing recomputes a bucket that already exists.
 */
export function isBucketClosed(bucketStart: Date, granularity: Granularity, now: Date): boolean {
  return bucketEndOf(bucketStart, granularity).getTime() <= now.getTime();
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

/**
 * A session is a run of events from one `visitor_id` with no gap longer than
 * this. **This is the only definition of a session in the product.**
 *
 * Thirty minutes is the industry's number and the reason to keep it is not
 * that it is correct — no gap is — but that it is the one a customer comparing
 * Panel against what they used before will expect, and an unexplained
 * difference in session counts reads as a bug in the new tool.
 *
 * Two consequences of Panel's identity model that fall out of this and are not
 * defects:
 *
 *  - **A session cannot cross midnight UTC.** The salt rotates, the visitor id
 *    becomes unrelated, and a visit spanning the rotation is recorded as two
 *    sessions. A night-time visitor is therefore counted slightly twice. The
 *    alternative is a visitor id that survives the night, which is the one
 *    thing this product does not have.
 *  - **There is no session id in the database.** Sessions are derived at rollup
 *    from timestamps and never stored, so nothing in Postgres can be used to
 *    stitch two visits together after the fact.
 */
export const SESSION_INACTIVITY_MS = 30 * 60 * 1000;

/** Does an event at `atMs` begin a new session after one at `previousAtMs`? */
export function startsNewSession(previousAtMs: number | null, atMs: number): boolean {
  if (previousAtMs === null) return true;
  return atMs - previousAtMs > SESSION_INACTIVITY_MS;
}

/**
 * Split one visitor's ascending timestamps into sessions, as index ranges.
 *
 * Ranges rather than sliced arrays because the caller is holding whole event
 * rows and only wants to know where the first and last of each session are —
 * that is what an entry page and an exit page are.
 *
 * The input must already be sorted ascending; sorting here would hide the
 * caller's mistake, and the caller (the rollup) gets its order from the
 * database, where it is an index rather than a sort.
 */
export interface SessionRange {
  /** Index of the session's first event, inclusive. */
  readonly start: number;
  /** Index of the session's last event, inclusive. */
  readonly end: number;
}

export function sessionRanges(ascendingTimestampsMs: readonly number[]): SessionRange[] {
  const ranges: SessionRange[] = [];
  let start = -1;
  let previous: number | null = null;
  for (let i = 0; i < ascendingTimestampsMs.length; i += 1) {
    const at = ascendingTimestampsMs[i] as number;
    if (startsNewSession(previous, at)) {
      if (start !== -1) ranges.push({ start, end: i - 1 });
      start = i;
    }
    previous = at;
  }
  if (start !== -1) ranges.push({ start, end: ascendingTimestampsMs.length - 1 });
  return ranges;
}

/**
 * Which bucket a session belongs to: the one its **first** event falls in.
 *
 * A session that starts at 13:55 and ends at 14:10 is one session in the 13:00
 * bucket and none in the 14:00 one. Counting it in both would make the hourly
 * session counts sum to more sessions than happened, and the alternative —
 * counting fractions — is a number nobody can act on. Written down here
 * because it is the kind of rule that gets re-decided differently by whoever
 * writes the next read path.
 */
export function sessionBucketStart(firstEventAt: Date, granularity: Granularity): Date {
  return bucketStartOf(firstEventAt, granularity);
}

// ---------------------------------------------------------------------------
// Dimensions
// ---------------------------------------------------------------------------

/**
 * The breakdowns the dashboard reads without touching a raw event.
 *
 * One table keyed by `(bucket, dimension, value)` rather than one table per
 * breakdown: the fork grew a column per question and then needed a migration
 * for every new one. Adding `utm_content` here is a constant and a rollup case,
 * not a migration — which is exactly the "without a painful migration" the
 * brief asks for.
 *
 * `entry_path` and `exit_path` are the first and last pageview of a session,
 * which is why sessions are defined in this file and not in the rollup: the
 * breakdown cannot be computed without the definition.
 */
export const EVENT_DIMENSIONS = [
  'path',
  'entry_path',
  'exit_path',
  'referrer_host',
  'country',
  'device_type',
  'browser',
  'os',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'event_name',
  // The four below are DERIVED from `events.destination` and `events.name`
  // rather than read off a column of their own, and they are in this list
  // anyway — unlike `acquisition`, which is not. The distinction is whether
  // the stored value is a thing to show a customer: `r:instagram.com` is an
  // encoding and needs a service that knows how to read it, while
  // `instagram.com` and `pdf` are exactly what belongs on the screen. So these
  // go through the generic breakdown path, get a card for free, and cost a
  // rollup case each instead of an endpoint each.
  'outbound_host',
  'download_path',
  'download_extension',
  'not_found_path',
] as const;
export type EventDimension = (typeof EVENT_DIMENSIONS)[number];

export function isEventDimension(value: unknown): value is EventDimension {
  return typeof value === 'string' && (EVENT_DIMENSIONS as readonly string[]).includes(value);
}

/**
 * The dimension the rollup writes an arrival's ACQUISITION under, and the
 * reason it is not in the list above.
 *
 * `EVENT_DIMENSIONS` is what an event carries directly. This one is derived —
 * the referrer host and the paid/e-mail verdict on `utm_medium`, encoded into
 * one value by `channels.ts` so that one session produces exactly one row and
 * the channels therefore sum to the session count. It reaches
 * `dimension_stats` through the same pass and the same table, and it is
 * deliberately invisible to the generic `/breakdowns` endpoint and to the
 * board's dimension picker: both print the stored value verbatim, and
 * `r:instagram.com` is an encoding rather than a row to show a customer.
 *
 * The standing is exactly `GOAL_TOTAL_DIMENSION`'s: a value only the rollup
 * writes, read by one service that knows what it means.
 */
export const ACQUISITION_DIMENSION = 'acquisition';

/**
 * Everything `dimension_stats.dimension` may hold.
 *
 * The distinction that matters to a caller: `EVENT_DIMENSIONS` is what you may
 * ASK for, `STORED_DIMENSIONS` is what may be THERE. The rollup's accumulator
 * and the goal splits are typed on this one, and `GOAL_DIMENSIONS` is checked
 * against it — because the load-bearing property of a goal dimension is that
 * `dimension_stats` holds the same `(dimension, value)` to divide by, not that
 * a visitor's browser sent it.
 */
export type StoredDimension = EventDimension | typeof ACQUISITION_DIMENSION;

/**
 * Built with `concat` and a `@__PURE__`, and not with a spread, for the reason
 * spelled out at `SEARCH_ENGINE_SET` in `channels.ts`.
 *
 * A spread at module scope invokes an iterator, which esbuild cannot prove is
 * side-effect free, so `[...EVENT_DIMENSIONS, ACQUISITION_DIMENSION]` survived
 * tree-shaking — and dragged `EVENT_DIMENSIONS` into the browser script with
 * it, where no line of code has ever asked what a dimension is. `concat` is a
 * call expression, which the annotation can mark pure, and the whole thing
 * drops.
 */
export const STORED_DIMENSIONS: readonly StoredDimension[] = /* @__PURE__ */ (
  EVENT_DIMENSIONS as readonly StoredDimension[]
).concat(ACQUISITION_DIMENSION);

export function isStoredDimension(value: unknown): value is StoredDimension {
  return typeof value === 'string' && (STORED_DIMENSIONS as readonly string[]).includes(value);
}

/** The three columns `dimension_stats` counts in. */
export type BreakdownUnit = 'pageviews' | 'sessions' | 'events';

/**
 * Which column each dimension is ranked, measured and shared out by.
 *
 * **One map, read by the query and by the screen**, because the two being
 * different is a bug with no symptom: the reads used to order the top-N by
 * `pageviews` while the dashboard displayed and shared out `sessions`, so the
 * traffic-sources card was the eight referrers with the most hits, labelled
 * with their session counts, ranked in an order the numbers on screen did not
 * explain. Nothing failed; the card was simply the wrong eight rows.
 *
 * The unit is a statement about the question, not about the data:
 *
 *  - `path` and the technical dimensions count **page views** — "how often was
 *    this read", "what were these people served on".
 *  - `entry_path`, `exit_path`, `referrer_host` and the five UTM columns count
 *    **sessions**, because each is an attribute of an ARRIVAL. The rollup
 *    attributes them from the session's first event for exactly this reason,
 *    and one visit reloading a page five times is still one arrival.
 *  - `event_name` counts **events**: a custom event has no page views at all,
 *    so sharing the page-view denominator with it made `checkout.started` a
 *    percentage of something it is not a part of.
 *
 * It is also what makes the unattributed remainder computable — see
 * `readBreakdowns` in the API: a session carries at most one value per
 * dimension, so "sessions in the window" minus "sessions across every value"
 * is exactly the sessions that had none.
 */
export const DIMENSION_UNIT = {
  path: 'pageviews',
  entry_path: 'sessions',
  exit_path: 'sessions',
  referrer_host: 'sessions',
  country: 'pageviews',
  device_type: 'pageviews',
  browser: 'pageviews',
  os: 'pageviews',
  utm_source: 'sessions',
  utm_medium: 'sessions',
  utm_campaign: 'sessions',
  utm_term: 'sessions',
  utm_content: 'sessions',
  event_name: 'events',
  // Every one of these counts EVENTS, and none of them counts sessions — which
  // is the whole reason this map exists, because "sessions" is the plausible
  // wrong answer for all four. A visit that downloads the price list twice did
  // download it twice; a visit that hits three broken URLs found three broken
  // URLs. Counting them per arrival would answer "how many visits contained at
  // least one" — a question nobody asked, indistinguishable on screen from the
  // one they did, and off by exactly the repeat clicks that make a link worth
  // reporting on.
  //
  // They cannot count `pageviews` either: a `$outbound` is not a `$pageview`,
  // so that column is zero on every one of these rows and a card ranked by it
  // would be permanently empty. `not_found_path` is the one that looks like an
  // exception and is not — the 404 page's own page view is counted under
  // `path`, and this dimension counts the `$404` events beside it.
  outbound_host: 'events',
  download_path: 'events',
  download_extension: 'events',
  not_found_path: 'events',
} as const satisfies Record<EventDimension, BreakdownUnit>;

/**
 * The cap on how many distinct values one `(bucket, dimension)` may keep.
 *
 * A public write endpoint means the set of paths is whatever anybody puts in a
 * URL bar, so this is the row count of the breakdown table and not a display
 * concern. `scrubPath` already collapses identifier segments, which is what
 * makes the cap generous rather than tight; past it the rollup stops adding new
 * values and says so in its log, rather than writing a million rows for one
 * site's 404 scanner.
 */
export const MAX_DIMENSION_VALUES_PER_BUCKET = 2_000;

// ---------------------------------------------------------------------------
// Metrics, and the one that cannot be added up
// ---------------------------------------------------------------------------

/** What one closed bucket holds. Mirrors the columns of `traffic_stats`. */
export interface BucketTotals {
  readonly pageviews: number;
  readonly events: number;
  readonly sessions: number;
  /** Distinct `visitor_id` inside this bucket. Exact here, and only here. */
  readonly visitors: number;
}

/**
 * Metrics that may be summed across buckets, because each event is counted in
 * exactly one bucket.
 */
export const ADDITIVE_METRICS = ['pageviews', 'events', 'sessions'] as const;

/**
 * Metrics that may not, because the same person can be counted in two buckets
 * and nothing left in the database can tell that they were the same person.
 */
export const NON_ADDITIVE_METRICS = ['visitors'] as const;

export type AdditiveMetric = (typeof ADDITIVE_METRICS)[number];
export type NonAdditiveMetric = (typeof NON_ADDITIVE_METRICS)[number];
export type Metric = AdditiveMetric | NonAdditiveMetric;

export function isAdditiveAcrossBuckets(metric: Metric): boolean {
  return (ADDITIVE_METRICS as readonly string[]).includes(metric);
}

/** Why a range has no unique-visitor count. A code, not a sentence: the copy is
 *  the dashboard's and goes through i18n like everything else it renders. */
export type VisitorsUnavailableReason = 'multiple_buckets';

/**
 * Totals over a range of buckets.
 *
 * `visitors` is `number | null` in the type, so that a caller who forgets the
 * rule does not compile. That is the entire point of this shape: the fork's
 * equivalent returned a number and the "unique visitors, last 30 days" tile it
 * fed was a sum of thirty daily figures, which is a count of visits with a
 * misleading label on it.
 */
export interface RangeTotals {
  readonly pageviews: number;
  readonly events: number;
  readonly sessions: number;
  readonly visitors: number | null;
  readonly visitorsUnavailableReason: VisitorsUnavailableReason | null;
  /** How many buckets went in. Zero is an empty range, not an error. */
  readonly buckets: number;
}

/**
 * Add the additive metrics and refuse to add the one that is not.
 *
 * A single bucket passes its own exact `visitors` through, which is what makes
 * "unique visitors today" and "unique visitors in the 14:00 hour" real numbers
 * while "unique visitors this month" is not.
 */
export function combineBuckets(buckets: readonly BucketTotals[]): RangeTotals {
  let pageviews = 0;
  let events = 0;
  let sessions = 0;
  for (const bucket of buckets) {
    pageviews += bucket.pageviews;
    events += bucket.events;
    sessions += bucket.sessions;
  }
  const single = buckets.length === 1 ? (buckets[0] as BucketTotals) : null;
  return {
    pageviews,
    events,
    sessions,
    visitors: buckets.length === 0 ? 0 : single ? single.visitors : null,
    visitorsUnavailableReason: buckets.length > 1 ? 'multiple_buckets' : null,
    buckets: buckets.length,
  };
}

// ---------------------------------------------------------------------------
// The ranges the dashboard reads over
// ---------------------------------------------------------------------------

/**
 * The three windows the period selector offers.
 *
 * Three and not seven, because each one has to be a window whose numbers the
 * product can actually defend. `24h` is the only hourly one; the other two are
 * whole UTC days, which is the grain `dimension_stats` is written at and the
 * grain the salt rotates on.
 *
 * Defined here rather than in the dashboard because the API validates the same
 * string it receives on the wire. Two lists would let the selector offer a
 * fourth value that the server answers 400 to.
 */
export const STAT_RANGES = ['24h', '7d', '30d'] as const;
export type StatRange = (typeof STAT_RANGES)[number];

export function isStatRange(value: unknown): value is StatRange {
  return typeof value === 'string' && (STAT_RANGES as readonly string[]).includes(value);
}

/** The grain and the span of one range. */
const RANGE_SHAPE: Readonly<Record<StatRange, { granularity: Granularity; buckets: number }>> = {
  '24h': { granularity: 'hour', buckets: 24 },
  '7d': { granularity: 'day', buckets: 7 },
  '30d': { granularity: 'day', buckets: 30 },
};

/**
 * A range turned into the exact window a read runs over.
 *
 * The window **ends where the open bucket begins**, and that is the rule this
 * whole shape exists to hold. `isBucketClosed` forbids the rollup from writing
 * an unfinished bucket, so an unfinished bucket has no row; a read whose window
 * included it would render a live hour as a cliff down to zero and a reader
 * would report it as data loss. The fraction of time that is missing is served
 * by {@link StatRange} `24h`'s neighbour — the realtime counters — and, for a
 * day grain, by the partial point a caller may build from today's closed hours.
 */
export interface ResolvedStatRange {
  readonly range: StatRange;
  readonly granularity: Granularity;
  /** First bucket start, inclusive. */
  readonly from: Date;
  /** Exclusive end: the start of the bucket `now` falls in, which is still open. */
  readonly to: Date;
  /** How many buckets sit in `[from, to)`. */
  readonly buckets: number;
  /** Start of the equal-length window immediately before `from`, for deltas. */
  readonly previousFrom: Date;
}

export function resolveStatRange(range: StatRange, now: Date = new Date()): ResolvedStatRange {
  const { granularity, buckets } = RANGE_SHAPE[range];
  const width = granularity === 'hour' ? HOUR_MS : DAY_MS;
  const to = bucketStartOf(now, granularity);
  const from = new Date(to.getTime() - buckets * width);
  return {
    range,
    granularity,
    from,
    to,
    buckets,
    previousFrom: new Date(from.getTime() - buckets * width),
  };
}

/**
 * Every bucket start in `[from, to)`, in order.
 *
 * Reads zero-fill with this rather than returning only the rows that exist. A
 * chart drawn from present rows alone silently rescales its x-axis when a site
 * goes quiet for two days, so a gap in traffic looks like a shorter period
 * instead of like a gap — and "did we stop receiving?" is a question this
 * product has to answer legibly.
 */
export function bucketStartsBetween(from: Date, to: Date, granularity: Granularity): Date[] {
  const width = granularity === 'hour' ? HOUR_MS : DAY_MS;
  const out: Date[] = [];
  for (let t = from.getTime(); t < to.getTime(); t += width) out.push(new Date(t));
  return out;
}

// ---------------------------------------------------------------------------
// Goals, funnels and revenue
//
// Re-exported rather than written here because this file is already the
// longest in the package and the two halves are read by different people: this
// one answers "what is a bucket and what may be added to what", `goals.ts`
// answers "what did somebody do and what was it worth". They share the
// vocabulary above and nothing else.
// ---------------------------------------------------------------------------
export * from './goals.js';

// ---------------------------------------------------------------------------
// Acquisition channels
//
// Re-exported for the same reason `goals.ts` is: the lists are long, they are
// edited by a different hand than the bucket arithmetic above, and a vendor
// asking for their search engine to be added should be able to open one file.
// `channels.ts` imports the constants above; nothing above imports it.
// ---------------------------------------------------------------------------
export * from './channels.js';

// ---------------------------------------------------------------------------
// Filters
//
// Re-exported here for the same reason the two above are, and carrying one
// extra warning that belongs next to the export rather than inside the file:
// `filters.ts` imports from this module TYPE-ONLY. A value import there closes
// a runtime cycle — this line is hoisted above everything declared above it —
// and the symptom is a temporal-dead-zone throw at import time, in the browser
// script as well as on the server.
// ---------------------------------------------------------------------------
export * from './filters.js';
